type TenantBrandingSource = {
  name?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  subdomain?: string | null;
};

export interface TenantBranding {
  displayName: string;
  shortName: string;
  logoUrl: string | null;
  faviconUrl: string;
  primaryColor: string;
  primaryContrast: string;
  primarySoft: string;
  secondaryColor: string;
  secondarySoft: string;
  initials: string;
  portalTitle: string;
  portalSubtitle: string;
  portalHostLabel: string | null;
}

const DEFAULT_PRIMARY = "#14b8a6";
const DEFAULT_SECONDARY = "#64748b";
const DEFAULT_ACCENT = "#facc15";
const DEFAULT_DISPLAY_NAME = "Milestone School";
const DEFAULT_LOGO_URL = "/logo.png";
const DEFAULT_PORTAL_SUBTITLE = "Multi-school access";

const isHexColor = (value?: string | null) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(value || "").trim());

const normalizeHex = (value?: string | null, fallback = DEFAULT_PRIMARY) => {
  if (!isHexColor(value)) return fallback;
  const hex = String(value).trim();
  if (hex.length === 4) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toLowerCase();
  }
  return hex.toLowerCase();
};

const hexToRgb = (hex: string) => {
  const normalized = normalizeHex(hex);
  const parsed = normalized.replace("#", "");
  return {
    r: parseInt(parsed.slice(0, 2), 16),
    g: parseInt(parsed.slice(2, 4), 16),
    b: parseInt(parsed.slice(4, 6), 16)
  };
};

const toSoftRgba = (hex: string, alpha: number) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getContrastColor = (hex: string) => {
  const { r, g, b } = hexToRgb(hex);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155 ? "#0f172a" : "#ffffff";
};

const getInitials = (name: string) => {
  const words = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (words.length === 0) return "SP";
  return words.map((word) => word.charAt(0).toUpperCase()).join("");
};

const buildFallbackFavicon = (initials: string, background: string, foreground: string, accent = DEFAULT_ACCENT) => {
  const safeInitials = encodeURIComponent(initials.slice(0, 2) || "MS");
  const safeBackground = encodeURIComponent(background);
  const safeForeground = encodeURIComponent(foreground);
  const safeAccent = encodeURIComponent(accent);
  return `data:image/svg+xml,` +
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'>` +
    `<rect width='64' height='64' rx='14' fill='${safeBackground}'/>` +
    `<circle cx='51' cy='14' r='6' fill='${safeAccent}'/>` +
    `<text x='50%' y='52%' dominant-baseline='middle' text-anchor='middle' font-family='Arial,sans-serif' font-size='28' font-weight='700' fill='${safeForeground}'>${safeInitials}</text>` +
    `</svg>`;
};

export const getTenantBranding = (tenant?: TenantBrandingSource | null): TenantBranding => {
  const displayName = String(tenant?.name || "").trim() || DEFAULT_DISPLAY_NAME;
  const primaryColor = normalizeHex(tenant?.primary_color, DEFAULT_PRIMARY);
  const secondaryColor = normalizeHex(tenant?.secondary_color, DEFAULT_SECONDARY);
  const logoUrl = typeof tenant?.logo_url === "string" && tenant.logo_url.trim()
    ? tenant.logo_url.trim()
    : DEFAULT_LOGO_URL;
  const subdomain = String(tenant?.subdomain || "").trim();
  const initials = getInitials(displayName);
  const primaryContrast = getContrastColor(primaryColor);

  return {
    displayName,
    shortName: displayName,
    logoUrl,
    faviconUrl: logoUrl || buildFallbackFavicon(initials, primaryColor, primaryContrast),
    primaryColor,
    primaryContrast,
    primarySoft: toSoftRgba(primaryColor, 0.12),
    secondaryColor,
    secondarySoft: toSoftRgba(secondaryColor, 0.12),
    initials,
    portalTitle: `${displayName} Portal`,
    portalSubtitle: DEFAULT_PORTAL_SUBTITLE,
    portalHostLabel: subdomain && subdomain !== "default" ? `${subdomain}.portal` : null
  };
};
